import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Reply, Ban, HelpCircle, MessageSquare, Eye, Undo2 } from "lucide-react";
import { formatFechaCorta } from "@/lib/agenda";

/** PROMPT 139: lo que entró por correos que mandó el propio equipo (una respuesta a una agencia o
 * a un cliente que repetía los datos de la reserva) y la limpieza, con vista previa y marcha atrás.
 * Desde el PROMPT 138 esos correos ya no se procesan al llegar; esto limpia lo que entró antes. */

function cuando(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function Lista({ titulo, bloque, testid, render, nota }) {
  if (!bloque || bloque.total === 0) return null;
  return (
    <div className="border border-slate-200 rounded-md" data-testid={testid}>
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[12px] font-medium text-slate-700">
        {titulo} <span className="text-slate-400">({bloque.total.toLocaleString("es")})</span>
        {nota && <span className="block font-normal text-slate-500">{nota}</span>}
      </div>
      <ul className="max-h-40 overflow-y-auto divide-y divide-slate-100">
        {bloque.lista.map((x) => (
          <li key={x.id} className="px-3 py-1.5 text-[12px] text-slate-600">{render(x)}</li>
        ))}
      </ul>
      {bloque.total > bloque.lista.length && (
        <p className="px-3 py-1.5 text-[11px] text-slate-400 border-t border-slate-100">
          Se muestran {bloque.lista.length.toLocaleString("es")} de {bloque.total.toLocaleString("es")}.
        </p>
      )}
    </div>
  );
}

const fecha = (f) => (f ? formatFechaCorta(f) : "sin fecha");

export default function CorreosEquipoDialog({ open, onOpenChange, onDone }) {
  const [vista, setVista] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [incluirSinGemela, setIncluirSinGemela] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const cargar = async () => {
    setLoading(true);
    setVista(null);
    setConfirmando(false);
    try {
      const { data } = await api.get("/captura/correos-del-equipo", { timeout: 60000 });
      setVista(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setIncluirSinGemela(false);
      cargar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reservasADescartar = (vista?.con_gemela?.total || 0) + (incluirSinGemela ? vista?.sin_gemela?.total || 0 : 0);
  const accionesASacar = vista?.acciones?.total || 0;
  const hayAlgo = reservasADescartar + accionesASacar > 0;
  const ultima = vista?.ultima_limpieza;

  const aplicar = async () => {
    setTrabajando(true);
    try {
      const { data } = await api.post("/captura/correos-del-equipo/aplicar", null, {
        params: { incluir_sin_gemela: incluirSinGemela }, timeout: 120000,
      });
      toast.success(
        `${data.reservas_descartadas} reserva(s) descartada(s) de la Bandeja · ${data.acciones_resueltas} correo(s) fuera de Acciones.`
      );
      onDone?.();
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTrabajando(false);
      setConfirmando(false);
    }
  };

  const deshacer = async () => {
    setTrabajando(true);
    try {
      const { data } = await api.post("/captura/correos-del-equipo/deshacer", null, { timeout: 120000 });
      if (data.deshecha) {
        toast.success(
          `Limpieza deshecha: ${data.reservas_vuelven} reserva(s) vuelven a la Bandeja y ${data.acciones_vuelven} correo(s) a Acciones.`
        );
      } else {
        toast.info("No hay ninguna limpieza para deshacer.");
      }
      onDone?.();
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTrabajando(false);
    }
  };

  const tarjetas = vista
    ? [
        { key: "con_gemela", label: "Se descartan de la Bandeja", valor: vista.con_gemela.total, icon: Ban,
          cls: "text-rose-700 bg-rose-50 border-rose-200" },
        { key: "sin_gemela", label: "Sin reserva gemela", valor: vista.sin_gemela.total, icon: HelpCircle,
          cls: "text-amber-700 bg-amber-50 border-amber-200" },
        { key: "acciones", label: "Salen de Acciones", valor: vista.acciones.total, icon: MessageSquare,
          cls: "text-sky-700 bg-sky-50 border-sky-200" },
        { key: "revisar", label: "Para revisar a mano",
          valor: vista.activas.total + vista.cambios.total + vista.no_descartables.total, icon: Eye,
          cls: "text-slate-700 bg-slate-100 border-slate-200" },
      ]
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="correos-equipo-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Reply className="h-5 w-5" /> Correos del equipo
          </DialogTitle>
          <DialogDescription>
            Lo que el equipo manda desde una casilla propia a una agencia o a un cliente (por ejemplo, una respuesta
            que repite los datos de la reserva) ya no entra como reserva ni como pedido. Acá se limpia lo que entró
            antes. Mirá la lista primero: nada se borra y se puede deshacer.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : vista ? (
          <div className="space-y-3" data-testid="correos-equipo-resumen">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {tarjetas.map(({ key, label, valor, icon: Icon, cls }) => (
                <Card key={key} className={`p-3 border ${cls}`} data-testid={`correos-equipo-card-${key}`}>
                  <div className="flex items-center gap-1.5 text-[11px] font-medium">
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </div>
                  <p className="text-2xl font-bold mt-1">{valor.toLocaleString("es")}</p>
                </Card>
              ))}
            </div>

            <Lista
              titulo="Se descartan: la reserva ya está cargada"
              nota="Mismo pasajero y misma fecha que otra reserva que trajo la agencia, el cliente, Bókun o una persona."
              bloque={vista.con_gemela}
              testid="correos-equipo-lista-con-gemela"
              render={(x) => (
                <>
                  <span className="font-mono text-slate-500">{x.codigo_booking}</span> · {x.pasajero || "sin nombre"} ·{" "}
                  {x.tour || "sin tour"} · {fecha(x.fecha)} → ya está{" "}
                  <span className="font-mono text-slate-700">{x.gemela?.codigo_booking}</span>
                  {x.gemela?.en_bandeja ? " (también en la Bandeja)" : ` (${x.gemela?.estado})`}
                </>
              )}
            />
            <Lista
              titulo="Sin reserva gemela: quedan en la Bandeja"
              nota="No se encontró otra reserva del mismo pasajero y fecha. Revisalas: si son respuestas del equipo, marcá la casilla de abajo."
              bloque={vista.sin_gemela}
              testid="correos-equipo-lista-sin-gemela"
              render={(x) => (
                <>
                  <span className="font-mono text-slate-500">{x.codigo_booking}</span> · {x.pasajero || "sin nombre"} ·{" "}
                  {x.tour || "sin tour"} · {fecha(x.fecha)} · <span className="text-slate-500">{x.asunto || "(sin asunto)"}</span>
                </>
              )}
            />
            <Lista
              titulo="Salen de Acciones"
              bloque={vista.acciones}
              testid="correos-equipo-lista-acciones"
              render={(x) => (
                <>
                  {x.asunto || "(sin asunto)"} <span className="text-slate-400">· para {x.para || "—"}</span>
                </>
              )}
            />
            <Lista
              titulo="Reservas activas que salieron de un correo del equipo: revisalas a mano"
              nota="No se tocan: ya están en la agenda y pueden estar ocupando lugar."
              bloque={vista.activas}
              testid="correos-equipo-lista-activas"
              render={(x) => (
                <>
                  <span className="font-mono text-slate-500">{x.codigo_booking}</span> · {x.pasajero || "sin nombre"} ·{" "}
                  {x.tour || "sin tour"} · {fecha(x.fecha)} {x.hora || ""} · {x.estado}
                </>
              )}
            />
            <Lista
              titulo="Cambios o cancelaciones que salieron de un correo del equipo: revisalos a mano"
              nota="No se tocan: los ya aplicados cambiaron la reserva."
              bloque={vista.cambios}
              testid="correos-equipo-lista-cambios"
              render={(x) => (
                <>
                  <span className="font-mono text-slate-500">{x.codigo_booking || "—"}</span> · {x.tipo} · {x.estado} ·{" "}
                  <span className="text-slate-500">{x.asunto || "(sin asunto)"}</span>
                </>
              )}
            />
            <Lista
              titulo="En la Bandeja pero no se pueden descartar desde acá (confirmadas o reprogramadas)"
              bloque={vista.no_descartables}
              testid="correos-equipo-lista-no-descartables"
              render={(x) => (
                <>
                  <span className="font-mono text-slate-500">{x.codigo_booking}</span> · {x.pasajero || "sin nombre"} ·{" "}
                  {fecha(x.fecha)} · {x.estado}
                </>
              )}
            />

            {vista.sin_gemela.total > 0 && (
              <label className="flex items-start gap-2 text-[12px] text-slate-700 cursor-pointer" data-testid="correos-equipo-incluir-sin-gemela">
                <Checkbox
                  checked={incluirSinGemela}
                  onCheckedChange={(v) => { setIncluirSinGemela(v === true); setConfirmando(false); }}
                  className="mt-0.5"
                />
                <span>
                  Descartar también las {vista.sin_gemela.total.toLocaleString("es")} sin reserva gemela (revisá la lista
                  antes).
                </span>
              </label>
            )}

            <p className="text-xs text-slate-500 flex items-center gap-1.5 border-t border-slate-100 pt-2" data-testid="correos-equipo-al-llegar">
              <Eye className="h-3.5 w-3.5 shrink-0" />
              Desde el cambio, {vista.no_procesados_al_llegar.toLocaleString("es")} correo(s) del equipo no se procesaron
              al llegar. Casillas propias: {vista.casillas.join(", ")}.
            </p>
            {ultima && (
              <div className="flex items-center justify-between gap-2 text-xs text-slate-500" data-testid="correos-equipo-ultima">
                <span>
                  Última limpieza: {cuando(ultima.fecha)} · {ultima.reservas} reserva(s) · {ultima.acciones} correo(s)
                  {ultima.deshecha ? " · deshecha" : ""}
                </span>
                {!ultima.deshecha && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={deshacer}
                    disabled={trabajando}
                    data-testid="correos-equipo-deshacer"
                    className="gap-1.5 h-7"
                  >
                    <Undo2 className="h-3.5 w-3.5" /> Deshacer la última limpieza
                  </Button>
                )}
              </div>
            )}
            {confirmando && (
              <p className="text-[12px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="correos-equipo-confirmacion">
                Se van a descartar {reservasADescartar.toLocaleString("es")} reserva(s) de la Bandeja y sacar{" "}
                {accionesASacar.toLocaleString("es")} correo(s) de Acciones. ¿Seguís?
              </p>
            )}
          </div>
        ) : null}

        <DialogFooter>
          {confirmando ? (
            <>
              <Button variant="outline" onClick={() => setConfirmando(false)} disabled={trabajando} data-testid="correos-equipo-volver">
                Volver
              </Button>
              <Button onClick={aplicar} disabled={trabajando} data-testid="correos-equipo-confirmar">
                {trabajando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sí, aplicar"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="correos-equipo-cerrar">
                Cerrar
              </Button>
              <Button
                onClick={() => setConfirmando(true)}
                disabled={trabajando || loading || !hayAlgo}
                data-testid="correos-equipo-aplicar"
              >
                Aplicar limpieza
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
